// NeonOracleSpotTheError — Tier-A "Neon Oracle" renderer for the spot-the-error
// component (the world's SECOND home-set pairing, after scenario-simulation).
//
// Reuses the existing Neon Oracle toolkit registration (atmosphere + audio cue
// palette in lib/scenario-themes/) — no new world infra. Reframes spot-the-error
// as an ANOMALY-DETECTION TERMINAL: each section is an intercepted transmission
// being scanned for corrupted data points; confirming an error locks it on with a
// glitch + drops a diagnostic log line; clicking a decoy reads as a FALSE POSITIVE.
//
// State machine + onActivityResponse mirror SpotTheErrorSlide's SpotTheErrorStudent
// exactly: `currentSectionIdx` + `foundErrors` + `falsePositives`. CRITICAL: every
// chunk (text, error, decoy) renders through ONE uniform `.nse-chunk` token so the
// styling never reveals the answers before a click — the same invariant the
// standard renderer enforces with its single ChunkSpan. On the final section it
// emits the EXACT
//   { activityType:'spot-the-error', mode:'quiz',
//     responseData:{ found:[errorId…], missed:[errorId…], falsePositives:[chunkKey…] } }
// payload, where found = caught errors, missed = errors NOT caught (declare-done),
// and falsePositives = wrongly-flagged chunks (plain text or decoy).

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { SpotTheErrorSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { useThemeWorldAudio } from '../../../../../lib/scenario-themes/audio';
import { WorldStage } from '../../../../../lib/scenario-themes/WorldStage';
import { ExperienceClose } from '../../shared/ExperienceClose';

const STYLE_ID = 'neon-ste-scoped-styles';

// Palette comes from the theme tokens, NOT from constants here. This file used
// to hardcode its own copy, which had already drifted from the world it claimed
// to be: DIM was #6f7d86 against the token's #6b7689, and body text #cfe8ef
// against #e8eef7. Two of the four copies of the same palette, disagreeing.
interface Ink {
  void: string;
  surface: string;
  text: string;
  lime: string;
  cyan: string;
  red: string;
  dim: string;
}

function inkFrom(theme: Theme): Ink {
  return {
    void: theme.colors.background,
    surface: theme.colors.surface,
    text: theme.colors.text,
    lime: theme.colors.primary,
    cyan: theme.colors.accent || theme.colors.primary,
    red: theme.colors.feedback.incorrectBorder,
    dim: theme.colors.textMuted,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced `.neonste`; injected once, ref-counted.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(ink: Ink): string {
  const { lime: LIME, cyan: CYAN, red: RED, dim: DIM, void: VOID, surface: SURFACE } = ink;
  return `
.neonste { position: relative; }
.neonste * { box-sizing: border-box; }

.neonste .nse-cursor { display: inline-block; width: 0.6ch; background: ${LIME}; animation: nseBlink 1.05s steps(1) infinite; }
@keyframes nseBlink { 0%,50% { opacity: 1; } 50.01%,100% { opacity: 0; } }

.neonste .nse-bar { height: 4px; background: ${CYAN}1f; border: 1px solid ${CYAN}40; border-radius: 999px; overflow: hidden; }
.neonste .nse-bar-fill { height: 100%; background: linear-gradient(90deg, ${CYAN}, ${LIME}); box-shadow: 0 0 10px ${LIME}; transition: width .5s cubic-bezier(.2,.8,.2,1); }

.neonste .nse-frame {
  position: relative; border: 1px solid ${CYAN}59; border-radius: 6px;
  background: ${SURFACE}9e; box-shadow: 0 0 0 1px ${CYAN}0f, 0 12px 40px -20px ${CYAN}66, inset 0 0 60px ${CYAN}08;
  backdrop-filter: blur(2px);
}
.neonste .nse-frame::before { content: ""; position: absolute; top: 0; left: 14px; right: 14px; height: 1px; background: linear-gradient(90deg, transparent, ${CYAN}66, transparent); }

.neonste .nse-readout { font-family: inherit; }

/* ── Scan token ───────────────────────────────────────────────────────────────
   EVERY scannable word (text, error, decoy) renders through ONE class so the
   learner cannot tell errors/decoys/plain text apart before clicking. A single
   uniform neon treatment (faint cyan dotted baseline + pointer) reads as
   "scannable data point", not "this one is the answer". State-specific classes
   (.found / .flash) are only added AFTER a click. Mirrors the standard
   SpotTheErrorSlide's single ChunkSpan. */
.neonste .nse-chunk { cursor: pointer; color: inherit; border-bottom: 1px dotted ${CYAN}47; padding: 0 2px; border-radius: 2px; transition: background .15s, color .15s, border-color .15s; }
.neonste .nse-chunk:hover { background: ${CYAN}1a; border-bottom-color: ${CYAN}80; }
.neonste .nse-chunk.locked { cursor: default; }
.neonste .nse-chunk.locked:hover { background: transparent; border-bottom-color: ${CYAN}47; }

/* caught error — lime lock-on (post-click only) */
.neonste .nse-chunk.found { color: ${LIME}; border-bottom: none; background: ${LIME}1f; text-shadow: 0 0 9px ${LIME}8c; cursor: default; }
.neonste .nse-chunk.found::before { content: "◢ "; color: ${LIME}; }
.neonste .nse-chunk.glitch { animation: nseGlitch .32s steps(2) 1; }
@keyframes nseGlitch {
  0% { text-shadow: -2px 0 ${RED}, 2px 0 ${CYAN}; transform: translateX(0); }
  25% { text-shadow: 2px 0 ${RED}, -2px 0 ${CYAN}; transform: translateX(1px); }
  50% { text-shadow: -2px 0 ${CYAN}, 2px 0 ${RED}; transform: translateX(-1px); }
  100% { text-shadow: 0 0 9px ${LIME}8c; transform: translateX(0); }
}

/* false positive — red shake (post-click only; plain text + decoy alike) */
.neonste .nse-chunk.flash { color: ${RED}; border-bottom-color: ${RED}80; background: ${RED}29; text-shadow: 0 0 8px ${RED}99; animation: nseShake .4s ease; }
@keyframes nseShake { 0%,100% { transform: translateX(0); } 20% { transform: translateX(-3px); } 40% { transform: translateX(3px); } 60% { transform: translateX(-2px); } 80% { transform: translateX(2px); } }

/* log lines */
.neonste .nse-log { border-top: 1px dashed ${CYAN}38; margin-top: 16px; padding-top: 12px; }
.neonste .nse-logline { display: block; font-size: 0.82rem; line-height: 1.5; color: ${DIM}; margin-bottom: 6px; opacity: 0; animation: nseLogIn .4s ease forwards; }
.neonste .nse-logline b { color: ${LIME}; }
@keyframes nseLogIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }

.neonste .nse-btn {
  font-family: inherit; text-transform: uppercase; letter-spacing: 0.14em; font-size: 0.82rem; cursor: pointer;
  color: ${VOID}; background: ${LIME}; border: none; border-radius: 4px; padding: 12px 20px;
  box-shadow: 0 0 18px ${LIME}80; transition: transform .15s, box-shadow .15s, filter .15s;
}
.neonste .nse-btn:hover { transform: translateY(-1px); box-shadow: 0 0 26px ${LIME}b3; filter: brightness(1.05); }
.neonste .nse-btn:active { transform: translateY(1px); }

.neonste .nse-cleared { animation: nseLogIn .45s ease both; }

@media (prefers-reduced-motion: reduce) {
  .neonste .nse-cursor { animation: none; }
  .neonste .nse-chunk.glitch, .neonste .nse-chunk.flash, .neonste .nse-logline, .neonste .nse-cleared { animation-duration: .001s !important; }
}
`;
}

function useScopedStyles(ink: Ink): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss(ink);
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function NeonOracleSpotTheError({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: SpotTheErrorSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();
  const ink = useMemo(() => inkFrom(theme), [theme]);
  useScopedStyles(ink);
  const { enabled: audioOn, toggle: toggleAudio, play } = useThemeWorldAudio('neon-oracle');

  const headingFont = theme.fonts.heading;
  const mono = theme.fonts.code || theme.fonts.body;
  const bodyFont = theme.fonts.body;

  // A saved payload is only written on the final "finalise diagnostic" action,
  // so its presence means "restore the submitted end-state". `found` is the list
  // of caught error ids; rehydrate the finished view from it.
  const savedDone = !!savedResponse && Array.isArray(savedResponse.found);

  // ── State machine (mirrors SpotTheErrorStudent) ──────────────────────────────
  const [currentSectionIdx, setCurrentSectionIdx] = useState(
    savedDone ? Math.max(0, data.sections.length - 1) : 0,
  );
  const [foundErrors, setFoundErrors] = useState<Set<string>>(
    () =>
      new Set(
        savedDone
          ? (savedResponse!.found as unknown[]).filter((id): id is string => typeof id === 'string')
          : [],
      ),
  );
  // Every wrongly-flagged chunk (plain text OR decoy) is a false positive — the
  // standard renderer tracks these uniformly via handleWrongClick, and so do we.
  const [falsePositives, setFalsePositives] = useState<Set<string>>(
    () =>
      new Set(
        savedDone && Array.isArray(savedResponse!.falsePositives)
          ? (savedResponse!.falsePositives as unknown[]).filter((id): id is string => typeof id === 'string')
          : [],
      ),
  );
  const [flash, setFlash] = useState<{ key: string; message: string } | null>(null);
  const [glitchId, setGlitchId] = useState<string | null>(null);
  const [allDone, setAllDone] = useState(savedDone);
  const completedRef = useRef(savedDone);

  const sectionErrorIds = useMemo(
    () =>
      data.sections.map((s) =>
        s.content.filter((c) => c.type === 'error' && c.errorId).map((c) => c.errorId!),
      ),
    [data.sections],
  );

  // Zero-error sections are vacuously complete — the declare-done button
  // advances them either way (mirrors SpotTheErrorStudent.isSectionComplete).
  const isSectionComplete = (idx: number): boolean => {
    const ids = sectionErrorIds[idx] ?? [];
    return ids.every((id) => foundErrors.has(id));
  };

  const handleErrorClick = useCallback(
    (errorId: string, sIdx: number) => {
      if (foundErrors.has(errorId)) return;
      setFoundErrors((prev) => {
        const nextFound = new Set(prev).add(errorId);
        const ids = sectionErrorIds[sIdx] ?? [];
        const sectionNowComplete = ids.length > 0 && ids.every((id) => nextFound.has(id));
        // anomaly lock-on cue; a fuller cue when it clears the packet
        play(sectionNowComplete ? 'commit' : 'reveal');
        return nextFound;
      });
      setGlitchId(errorId);
      window.setTimeout(() => setGlitchId((c) => (c === errorId ? null : c)), 340);
    },
    [foundErrors, sectionErrorIds, play],
  );

  // Wrong click — plain text OR a decoy. Both read as a FALSE POSITIVE: a valid
  // data point mistaken for corruption. Behaviourally identical so neither the
  // decoy nor plain text is distinguishable from the other before a click.
  //
  // The message matters. A decoy is the most teachable moment in the whole
  // exercise (this LOOKS wrong and isn't, and here's why), and the author writes
  // that reason into the decoy's `explanation`. It used to be thrown away in
  // favour of a fixed string on a native `title` tooltip — invisible on touch,
  // a second's hover delay on desktop. Now it's a real visible readout, matching
  // the standard renderer's flash.
  const handleWrongClick = useCallback(
    (chunkKey: string, message: string) => {
      setFalsePositives((prev) => new Set(prev).add(chunkKey));
      setFlash({ key: chunkKey, message });
      play('reset'); // descending "rejected" sweep
      window.setTimeout(() => setFlash((c) => (c?.key === chunkKey ? null : c)), 2600);
    },
    [play],
  );

  const advanceSection = useCallback(() => {
    if (currentSectionIdx < data.sections.length - 1) {
      setCurrentSectionIdx((i) => i + 1);
      play('advance');
    } else {
      setAllDone(true);
      play('reveal');
      // Payload mirrors SpotTheErrorStudent exactly: found = caught error ids,
      // missed = error ids NOT caught (declare-done lets the learner finish
      // without catching them all), falsePositives = wrongly-flagged chunks.
      const allErrorIds = sectionErrorIds.flat();
      onActivityResponse?.({
        activityType: 'spot-the-error',
        mode: 'quiz',
        responseData: {
          found: allErrorIds.filter((id) => foundErrors.has(id)),
          missed: allErrorIds.filter((id) => !foundErrors.has(id)),
          falsePositives: Array.from(falsePositives),
        },
      });
      // Terminal completion point — the final "finalise diagnostic" action.
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
    }
  }, [currentSectionIdx, data.sections.length, sectionErrorIds, foundErrors, falsePositives, onActivityResponse, onComplete, play]);

  const totalErrors = sectionErrorIds.reduce((acc, ids) => acc + ids.length, 0);
  const pct = totalErrors > 0 ? Math.round((foundErrors.size / totalErrors) * 100) : 0;

  // Same default the standard renderer uses, so a slide authored without an
  // explicit conclusion still lands a close rather than nothing.
  const conclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'So what',
    body: 'You caught {found} of {total} planted errors.',
  };

  const { lime: LIME, cyan: CYAN, red: RED, dim: DIM } = ink;

  return (
    <WorldStage
      worldId="neon-oracle"
      theme={theme}
      audioEnabled={audioOn}
      onToggleAudio={toggleAudio}
      className="neonste"
    >
      <div className="relative">
        <div style={{ maxWidth: 780, margin: '0 auto', padding: '78px 22px 100px' }}>
          {/* header */}
          <header style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: mono, fontSize: '0.7rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: CYAN, marginBottom: 14 }}>
              ◇ ORACLE · Anomaly Scan
            </div>
            <h1 style={{ fontFamily: headingFont, fontWeight: 700, fontSize: 'clamp(1.6rem, 4.5vw, 2.6rem)', lineHeight: 1.1, color: LIME, textShadow: `0 0 18px ${LIME}55`, margin: '0 0 10px' }}>
              {data.title}
            </h1>
            {data.subtitle && (
              <p style={{ fontFamily: bodyFont, fontSize: '0.95rem', color: DIM, margin: 0 }}>
                <span style={{ fontFamily: mono, color: CYAN }}>&gt;</span> {data.subtitle}
                <span className="nse-cursor" aria-hidden>&nbsp;</span>
              </p>
            )}
            <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="nse-bar" style={{ flex: 1 }} aria-hidden>
                <div className="nse-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <span style={{ fontFamily: mono, fontSize: '0.74rem', color: CYAN, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
                {foundErrors.size}/{totalErrors} isolated
              </span>
            </div>
          </header>

          {/* sections / transmissions */}
          {data.sections.map((section, sIdx) => {
            if (sIdx > currentSectionIdx) return null;
            const errorIds = sectionErrorIds[sIdx] ?? [];
            const foundCount = errorIds.filter((id) => foundErrors.has(id)).length;
            const complete = isSectionComplete(sIdx);
            const isCurrent = sIdx === currentSectionIdx;

            // accumulate the diagnostic log for found errors in this section
            const logEntries = section.content
              .filter((c) => c.type === 'error' && c.errorId && foundErrors.has(c.errorId) && c.explanation)
              .map((c) => ({ id: c.errorId!, text: c.explanation! }));

            return (
              <section key={section.id} className="nse-frame" style={{ padding: '22px 24px', marginBottom: 22, opacity: !isCurrent && complete ? 0.72 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 12 }}>
                  <span style={{ fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: CYAN }}>
                    ▸ Transmission {String(sIdx + 1).padStart(2, '0')} · {section.instruction}
                  </span>
                  <span style={{ fontSize: '0.7rem', letterSpacing: '0.08em', color: complete ? LIME : DIM, border: `1px solid ${complete ? LIME : `${DIM}66`}`, borderRadius: 999, padding: '2px 10px', whiteSpace: 'nowrap' }}>
                    {foundCount}/{errorIds.length} ◢
                  </span>
                </div>
                <p style={{ fontSize: '0.82rem', color: DIM, margin: '0 0 16px' }}>{section.context}</p>

                <div className="nse-readout" style={{ fontFamily: mono, lineHeight: 1.85, fontSize: '0.98rem', color: ink.text }}>
                  {section.content.map((chunk, cIdx) => {
                    // ── LEAK FIX ──────────────────────────────────────────────
                    // EVERY chunk — text, error, decoy — renders through ONE
                    // branch with identical pre-click styling (.nse-chunk),
                    // role, tabIndex and cursor, so the markup never reveals
                    // which words are errors/decoys. Only AFTER a click do the
                    // .found (caught error) / .flash (false positive) state
                    // classes diverge. Mirrors SpotTheErrorSlide's single
                    // ChunkSpan for all chunk types.
                    const isError = chunk.type === 'error' && !!chunk.errorId;
                    const found = isError && foundErrors.has(chunk.errorId!);
                    const glitching = isError && glitchId === chunk.errorId;
                    // Stable key for non-error (text + decoy) false-positive tracking.
                    const wrongKey = `${section.id}:${cIdx}`;
                    const flashing = !found && flash?.key === wrongKey;
                    // A chunk is interactive while its section is current and it
                    // hasn't already been caught (caught errors lock).
                    const interactive = isCurrent && !found;

                    const activate = () => {
                      if (!interactive) return;
                      if (isError) handleErrorClick(chunk.errorId!, sIdx);
                      else
                        handleWrongClick(
                          wrongKey,
                          chunk.type === 'decoy' && chunk.explanation
                            ? chunk.explanation
                            : 'this data point is valid.',
                        );
                    };

                    return (
                      <span
                        key={cIdx}
                        role="button"
                        tabIndex={interactive ? 0 : -1}
                        aria-pressed={found}
                        className={`nse-chunk${interactive ? '' : ' locked'}${found ? ' found' : ''}${glitching ? ' glitch' : ''}${flashing ? ' flash' : ''}`}
                        onClick={activate}
                        onMouseEnter={() => interactive && play('hover')}
                        onKeyDown={(e) => {
                          if (!interactive) return;
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
                        }}
                      >
                        {chunk.text}
                      </span>
                    );
                  })}
                </div>

                {/* False-positive readout — the authored reason this valid-looking
                    data point is actually fine. Live region so it's announced,
                    not just flashed. */}
                {isCurrent && flash && (
                  <div
                    role="status"
                    className="nse-cleared"
                    style={{
                      marginTop: 14,
                      padding: '10px 14px',
                      borderRadius: 4,
                      background: `${RED}1a`,
                      border: `1px solid ${RED}66`,
                      color: ink.text,
                      fontFamily: bodyFont,
                      fontSize: '0.86rem',
                      lineHeight: 1.55,
                    }}
                  >
                    <span style={{ color: RED, fontFamily: mono, letterSpacing: '0.12em' }}>
                      FALSE POSITIVE —{' '}
                    </span>
                    {flash.message}
                  </div>
                )}

                {/* diagnostic log */}
                {logEntries.length > 0 && (
                  <div className="nse-log">
                    <div style={{ fontFamily: mono, fontSize: '0.68rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: CYAN, marginBottom: 8 }}>▾ Anomaly Log</div>
                    {logEntries.map((e) => (
                      <span key={e.id} className="nse-logline" style={{ fontFamily: bodyFont }}>
                        <b style={{ fontFamily: mono }}>» {e.id}</b> — {e.text}
                      </span>
                    ))}
                  </div>
                )}

                {complete && (
                  <div className="nse-cleared" style={{ marginTop: 16, padding: '10px 14px', borderRadius: 4, background: `${LIME}1a`, border: `1px solid ${LIME}55`, color: LIME, fontFamily: bodyFont, fontSize: '0.86rem' }}>
                    ✓ Packet cleared — {section.completionMessage}
                  </div>
                )}

                {isCurrent && !allDone && (
                  <button className="nse-btn" style={{ marginTop: 16 }} onClick={advanceSection}>
                    {/* Declare-done: learner can advance/finalise WITHOUT having
                        caught every anomaly (mirrors SpotTheErrorStudent's
                        "I've found them all" button). */}
                    {complete
                      ? sIdx < data.sections.length - 1
                        ? '> Advance scan'
                        : '> Finalise diagnostic'
                      : sIdx < data.sections.length - 1
                        ? '> Scan complete — advance'
                        : '> Scan complete — finalise'}
                  </button>
                )}
              </section>
            );
          })}

          {/* diagnostic summary */}
          {allDone && (
            <section className="nse-frame nse-cleared" style={{ padding: '30px 26px', textAlign: 'center' }}>
              <div style={{ fontFamily: mono, fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: CYAN, marginBottom: 12 }}>Diagnostic complete</div>
              <h2 style={{ fontFamily: headingFont, fontWeight: 700, fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', color: LIME, textShadow: `0 0 16px ${LIME}55`, margin: '0 0 8px' }}>
                {foundErrors.size} / {totalErrors} anomalies isolated
              </h2>
              {falsePositives.size > 0 && (
                <p style={{ color: DIM, fontFamily: bodyFont, fontSize: '0.86rem', margin: 0 }}>
                  {falsePositives.size} false positive{falsePositives.size === 1 ? '' : 's'} flagged — valid data points mistaken for corruption.
                </p>
              )}
            </section>
          )}

          {/* The authored "So what?" close. A score readout is not a close: it
              says how the learner did, never what any of it was FOR. Every other
              spot-the-error render path (the standard renderer, and this world's
              own composed variant) lands on <ExperienceClose>; this one used to
              stop at the scoreboard and silently discard `data.conclusion`. */}
          {allDone && (
            <div style={{ marginTop: 18 }}>
              <ExperienceClose
                conclusion={conclusion}
                theme={theme}
                values={{ found: foundErrors.size, total: totalErrors }}
                fallback="Look back at which claims you flagged and which slipped past."
              />
            </div>
          )}
        </div>
      </div>
    </WorldStage>
  );
}
